<template>
  <div class="character-template-editor gm-page">
    <!-- Top Row (1) -->
    <div class="top-row gm-topbar">
      <div class="top-row__brand">
        <p class="gm-eyebrow">Game master</p>
        <h1 class="gm-title">Character Template Editor</h1>
      </div>
      <div class="toolbar">
        <button class="gm-btn gm-btn--primary" @click="saveTemplate" :disabled="isLoading">Save Template</button>
        <button class="gm-btn gm-btn--ghost" @click="cancelEditing" :disabled="isLoading">Cancel</button>
      </div>
    </div>

    <!-- Center Row (8) -->
    <div class="center-row">
      <!-- Center Column (2) -->
      <div class="center-column">
        <!-- Loading overlay -->
        <div v-if="isLoading" class="loading-overlay">
          <span class="dx-loader" aria-hidden="true"></span>
          <p>Loading...</p>
        </div>

        <!-- Error message -->
        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <!-- Validation errors -->
        <div v-if="validationErrors.length > 0" class="validation-errors">
          <h4>Validation Errors:</h4>
          <ul>
            <li v-for="(error, index) in validationErrors" :key="index">
              {{ error.message }}
            </li>
          </ul>
        </div>

        <!-- This column is divided into 1,8 for columns -->
        <div class="center-content">
          <!-- Left part (1) -->
          <div class="template-list gm-sidebar">
            <NPCTemplatesList
              class="npc-templates-list"
              title="Available Templates"
              @template-selected="onNPCTemplateSelected"
            />
          </div>

          <!-- Right part (8) -->
          <div class="template-editor" v-if="template">
            <!-- Tab Navigation -->
            <div class="gm-tabs">
              <button
                :class="{ 'is-active': activeTab === 'template' }"
                @click="activeTab = 'template'"
                class="gm-tab"
              >
                Template Details
              </button>
              <button
                :class="{ 'is-active': activeTab === 'limits' }"
                @click="activeTab = 'limits'"
                class="gm-tab"
              >
                Validation Limits
              </button>
            </div>

            <!-- Tab Content -->
            <div class="tab-content">
              <CharacterTemplateDetail
                v-if="activeTab === 'template'"
                :template="template"
                :service="service"
                @update="onTemplateUpdate"
              />
              <TemplateLimitsEditor
                v-if="activeTab === 'limits'"
                :template="template"
                :service="service"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column (1) -->
      <div class="right-column">
        <div class="sidebar-section">
          <h3 class="gm-section-title">Preview</h3>
          <TemplatePreviewCard v-if="template" :template="template" :service="service" />
        </div>

      </div>
    </div>

    <!-- Bottom Row (1) -->
    <div class="bottom-row">
      <div class="status-bar">
        <span>Last saved: {{ lastSaved ? lastSaved.toLocaleString() : 'Never' }}</span>
        <span>Status: {{ isDirty ? 'Unsaved Changes' : 'Saved' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { characterTemplateEditorService } from '@/services/CharacterTemplateEditorService.js';
import { characterTemplatesService } from '@/services/CharacterTemplatesService.ts';
import { createSampleCharacterTemplate } from '@/models/CharacterTemplateFull.js';
import NPCTemplatesList from '@/components/shared/NPCTemplatesList.vue';
import CharacterTemplateDetail from '@/components/GameMaster/CharacterTemplateDetail.vue';
import TemplatePreviewCard from '@/components/GameMaster/TemplatePreviewCard.vue';
import TemplateLimitsEditor from '@/components/GameMaster/TemplateLimitsEditor.vue';

export default {
  name: 'CharacterTemplateEditor',
  components: {
    NPCTemplatesList,
    CharacterTemplateDetail,
    TemplatePreviewCard,
    TemplateLimitsEditor
  },
  data() {
    return {
      service: characterTemplateEditorService,
      templatesService: characterTemplatesService,
      template: null,
      availableTemplates: [],
      isLoading: false,
      error: null,
      lastSaved: null,
      isDirty: false,
      validationErrors: [],
      activeTab: 'template'
    };
  },
  computed: {
    filteredTemplates() {
      return this.availableTemplates;
    },
    totalStatsPoints() {
      if (!this.template) return 0;
      return this.template.data.stats.reduce((sum, stat) => sum + stat.value, 0);
    },
    maxStatsPoints() {
      if (!this.template) return 100;
      return this.template.validation.max_stats_points_count;
    }
  },
  async created() {
    // Set up event listeners
    this.service.on('templateLoaded', this.onTemplateLoaded);
    this.service.on('templateUpdated', this.onTemplateUpdated);
    this.service.on('loadingFailed', this.onLoadingFailed);
    this.service.on('validationError', this.onValidationError);
    this.service.on('dirtyStateChanged', this.onDirtyStateChanged);
    this.service.on('previewGenerationStarted', this.onPreviewGenerationStarted);
    this.service.on('previewGenerated', this.onPreviewGenerated);
    this.service.on('previewGenerationFailed', this.onPreviewGenerationFailed);

    // Initialize the service
    await this.service.initialize();

    // Load available templates
    await this.loadAvailableTemplates();

    // Load a sample template for now
    this.template = createSampleCharacterTemplate();
  },
  beforeUnmount() {
    // Clean up event listeners
    this.service.off('templateLoaded', this.onTemplateLoaded);
    this.service.off('templateUpdated', this.onTemplateUpdated);
    this.service.off('loadingFailed', this.onLoadingFailed);
    this.service.off('validationError', this.onValidationError);
    this.service.off('dirtyStateChanged', this.onDirtyStateChanged);
    this.service.off('previewGenerationStarted', this.onPreviewGenerationStarted);
    this.service.off('previewGenerated', this.onPreviewGenerated);
    this.service.off('previewGenerationFailed', this.onPreviewGenerationFailed);
  },
  methods: {
    async loadAvailableTemplates() {
      try {
        this.isLoading = true;
        await this.templatesService.initialize();
        this.availableTemplates = this.templatesService.getTemplates();
        this.isLoading = false;
      } catch (error) {
        console.error('Failed to load available templates:', error);
        this.error = 'Failed to load available templates';
        this.isLoading = false;
      }
    },

    async importFromTemplate(templateId) {
      try {
        this.isLoading = true;
        this.error = null;
        this.validationErrors = [];

        await this.service.importFromTemplate(templateId);
        this.isLoading = false;
      } catch (error) {
        console.error('Failed to import template:', error);
        this.error = 'Failed to import template';
        this.isLoading = false;
      }
    },


    selectTemplate(template) {
      if (template && template.id) {
        this.importFromTemplate(template.id);
      }
    },

    updateTemplateName(event) {
      this.service.setName(event.target.value);
    },

    updateStat(statName, event) {
      const value = parseInt(event.target.value, 10);
      if (!isNaN(value)) {
        this.service.updateStat(statName, value);
      }
    },

    saveTemplate() {
      // Validate the template
      const validation = this.service.validate();
      if (!validation.isValid) {
        this.validationErrors = validation.errors;
        return;
      }

      // Export the template
      const exportedTemplate = this.service.export();
      if (exportedTemplate) {
        this.lastSaved = new Date();
        this.isDirty = false;
        console.log('Template saved:', exportedTemplate);
      }
    },

    cancelEditing() {
      if (this.isDirty) {
        if (confirm('You have unsaved changes. Are you sure you want to cancel?')) {
          this.service.reset();
          this.template = this.service.getTemplate();
          this.isDirty = false;
        }
      } else {
        this.service.reset();
        this.template = this.service.getTemplate();
      }
    },

    // Event handlers
    onTemplateLoaded(template) {
      this.template = template;
      this.error = null;
      this.validationErrors = [];
    },

    onTemplateUpdated(template) {
      this.template = template;
    },

    onLoadingFailed(error) {
      console.error('Loading failed:', error);
      this.error = 'Failed to load template';
    },

    onValidationError(errors) {
      this.validationErrors = Array.isArray(errors) ? errors : [errors];
    },

    onDirtyStateChanged(isDirty) {
      this.isDirty = isDirty;
    },

    /**
     * Handle template updates from the CharacterTemplateDetail component
     */
    onTemplateUpdate() {
      // The template will be updated automatically through the service
      // No additional action needed here as the service handles state management
    },

    /**
     * Handle NPC template selection from the NPCTemplatesList component
     * @param {Object} template - The selected NPC template
     */
    onNPCTemplateSelected(template) {
      console.log('NPC template selected:', template);

      // If we already have a template loaded, ask for confirmation before replacing it
      if (this.template && this.isDirty) {
        if (!confirm('You have unsaved changes. Are you sure you want to load a new template?')) {
          return;
        }
      }

      // Import the template data
      if (template && template.id) {
        this.importFromTemplate(template.id);
      }
    },

    /**
     * Handle preview generation started event
     */
    onPreviewGenerationStarted() {
      console.log('Preview generation started');
    },

    /**
     * Handle preview generated event
     * @param {Object} previewData - The generated preview data
     */
    onPreviewGenerated(previewData) {
      console.log('Preview generated successfully:', previewData);
    },

    /**
     * Handle preview generation failed event
     * @param {Object} errorData - The error information
     */
    onPreviewGenerationFailed(errorData) {
      console.warn('Preview generation failed:', errorData);
      // Could show a toast or error message to the user if needed
    }
  }
};
</script>

<style scoped>
.character-template-editor {
  height: 100vh;
  width: 100%;
  max-height: 100vh;
}

/* Top Row - 1 part of vertical layout */
.top-row {
  min-height: 60px;
}

.top-row__brand {
  display: flex;
  flex-direction: column;
}

.toolbar {
  display: flex;
  gap: var(--gm-gap-sm);
}

/* Center Row - 8 parts of vertical layout */
.center-row {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.center-column {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: var(--gm-surface-0);
}

.center-content {
  display: flex;
  height: 100%;
}

/* Left part of center column - 1 part */
.template-list {
  width: 20rem;
  padding-bottom: var(--gm-dock-clearance);
}

/* Right part of center column - 8 parts */
.template-editor {
  flex: 8;
  display: flex;
  flex-direction: column;
  background: var(--gm-surface-0);
}

/* Tab Content */
.tab-content {
  flex: 1;
  padding: var(--gm-gap-lg);
  overflow-y: auto;
  padding-bottom: var(--gm-dock-clearance);
}

.form-group {
  margin-bottom: var(--gm-gap-lg);
}

.form-group label {
  display: block;
  margin-bottom: 0.3rem;
  font-weight: bold;
  color: var(--gm-text-muted);
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group textarea {
  width: 100%;
  padding: 0.5rem;
  background: var(--gm-surface-sunken);
  color: var(--gm-text);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  transition: border-color 0.15s ease;
}

.form-group input[type="text"]:focus,
.form-group input[type="number"]:focus,
.form-group textarea:focus {
  border-color: var(--gm-border);
  outline: none;
}

.form-group textarea {
  height: 100px;
  resize: vertical;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--gm-gap-sm);
}

.stat-item {
  display: flex;
  flex-direction: column;
}

.stat-item label {
  margin-bottom: 0.3rem;
  color: var(--gm-text-muted);
}

.stat-item input {
  width: 100%;
  padding: 0.5rem;
  background: var(--gm-surface-sunken);
  color: var(--gm-text);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
}

.stat-item input:focus {
  border-color: var(--gm-border);
  outline: none;
}

/* Right Column - 1 part of horizontal layout */
.right-column {
  width: 300px;
  background: var(--gm-surface-1);
  border-left: 1px solid var(--gm-border-dim);
  padding: var(--gm-gap-lg);
  overflow-y: auto;
  padding-bottom: var(--gm-dock-clearance);
}

.preview-card {
  background: var(--gm-surface-2);
  border-radius: var(--gm-radius);
  padding: var(--gm-gap);
  border: 1px solid var(--gm-border-dim);
}

.preview-image {
  height: 150px;
  background: var(--gm-surface-2);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.6rem 0;
  border-radius: var(--gm-radius-sm);
  color: var(--gm-text-muted);
}

.preview-stats {
  font-size: 0.85rem;
  color: var(--gm-text-muted);
}

.preview-stats p {
  margin: 0.3rem 0;
}

.preview-tags {
  margin-top: 0.6rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.preview-tag {
  background: var(--gm-surface-2);
  color: var(--gm-text-muted);
  padding: 0.1rem 0.4rem;
  border-radius: var(--gm-radius-sm);
  font-size: 0.8rem;
}

/* NPC Templates Section */
.npc-templates-section {
  margin-top: var(--gm-gap-lg);
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.npc-templates-list {
  flex: 1;
  min-height: 300px;
  max-height: 500px;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  overflow: hidden;
}

/* Bottom Row - 1 part of vertical layout */
.bottom-row {
  height: 40px;
  background: var(--gm-surface-1);
  color: var(--gm-text);
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-top: 1px solid var(--gm-border-dim);
  min-height: 40px;
  font-size: 0.9rem;
}

.status-bar {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.status-bar span {
  color: var(--gm-text-muted);
}

/* Loading overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(12, 14, 15, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 100;
  color: var(--gm-text);
}

@media (prefers-reduced-motion: reduce) {
}

/* Error message */
.error-message {
  background: rgba(201, 69, 63, 0.14);
  color: var(--gm-danger);
  border: 1px solid rgba(201, 69, 63, 0.4);
  padding: var(--gm-gap-sm);
  margin: var(--gm-gap-sm);
  border-radius: var(--gm-radius-sm);
}

/* Validation errors */
.validation-errors {
  background: rgba(201, 69, 63, 0.1);
  color: var(--gm-danger);
  border: 1px solid rgba(201, 69, 63, 0.35);
  padding: var(--gm-gap-sm);
  margin: var(--gm-gap-sm);
  border-radius: var(--gm-radius-sm);
}

.validation-errors h4 {
  margin-top: 0;
  margin-bottom: 0.3rem;
}

.validation-errors ul {
  margin: 0;
  padding-left: 1.25rem;
}

/* No templates message */
.no-templates {
  padding: 0.6rem;
  color: var(--gm-text-muted);
  font-style: italic;
}

/* Tags container */
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.3rem;
}

.tag {
  background: var(--gm-surface-2);
  color: var(--gm-text);
  padding: 0.1rem 0.4rem;
  border-radius: var(--gm-radius-sm);
  display: flex;
  align-items: center;
}

.tag-remove {
  background: none;
  border: none;
  color: var(--gm-text-muted);
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0 0 0 0.3rem;
  margin: 0;
}

.tag-input {
  background: var(--gm-surface-sunken);
  color: var(--gm-text);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  padding: 0.1rem 0.4rem;
  min-width: 100px;
}

/* Input hint */
.input-hint {
  font-size: 0.8rem;
  color: var(--gm-text-muted);
  margin-left: 0.3rem;
}

/* Bio section */
.bio-section {
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.6rem;
}

.bio-field {
  flex: 1;
}

.bio-field.full-width {
  width: 100%;
  margin-bottom: 0.6rem;
}

.bio-field label {
  display: block;
  margin-bottom: 0.3rem;
  color: var(--gm-text-muted);
}

/* Items, Schools, Spells lists */
.items-list, .schools-list, .spells-list {
  background: var(--gm-surface-sunken);
  border-radius: var(--gm-radius-sm);
  padding: 0.6rem;
  margin-top: 0.3rem;
}

.item, .school, .spell {
  background: var(--gm-surface-2);
  color: var(--gm-text);
  padding: 0.3rem 0.6rem;
  border-radius: var(--gm-radius-sm);
  margin-bottom: 0.3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.item-remove, .school-remove, .spell-remove {
  background: none;
  border: none;
  color: var(--gm-text-muted);
  cursor: pointer;
  font-size: 1.2rem;
}

.add-item, .add-school, .add-spell {
  margin-top: 0.3rem;
}

.add-item input, .add-school input, .add-spell input {
  width: 100%;
  padding: 0.3rem;
  background: var(--gm-surface-2);
  color: var(--gm-text);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
}
</style>